/**
 * CASE-AUTO-002 (Sep 2026) — automatic readable casing for Search Fulfillment.
 *
 * Two rules share one word-level engine:
 *   Rule P  smartProperCase()       party / name / short descriptive fields
 *   Rule C  readableCaseComments()  free-form comments / notes
 * Both only ever change LETTER CASE — never punctuation, whitespace, digits,
 * newlines or token order. Tokens containing digits (instrument numbers,
 * dates, book/page refs, amounts) are never touched.
 *
 * autoCase(value, rule) applies a rule ONLY when the value is predominantly
 * ALL CAPS (see looksAllCaps); mixed-case input is returned untouched.
 * Backend save stays trim-only (utils/proper_case.py) so historical records
 * are never rewritten by autosave — only the field a processor edits.
 */
const UPPER_PARTY = new Set([
  'LLC', 'LLP', 'PLLC', 'LP', 'PC', 'PA', 'FSB', 'MERS', 'USA', 'US', 'NA',
  'HOA', 'IRS', 'FHA', 'VA', 'HUD', 'FNMA', 'FHLMC', 'GNMA',
  'AKA', 'FKA', 'NKA', 'DBA', 'POA', 'UCC', 'DOT', 'HELOC', 'REO', 'SBM', 'TTEE',
  'II', 'III', 'IV', 'VI', 'VII', 'VIII', 'IX',
  // state codes that are not English words / connectors
  'AK', 'AZ', 'CA', 'CT', 'DC', 'FL', 'GA', 'IA', 'IL', 'KS', 'KY', 'MD', 'MI', 'MN', 'MS', 'MT',
  'NC', 'ND', 'NE', 'NH', 'NJ', 'NM', 'NV', 'NY', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'WI', 'WV', 'WY', 'AR',
]);
const LOWER_PARTY = new Set(['of', 'and', 'the', 'for', 'as', 'a', 'an', 'in', 'on',
  'at', 'by', 'to', 'et', 'al', 'ux', 'vir', 'with', 'from', 'or']);

const UPPER_COMMENTS = new Set([...UPPER_PARTY,
  'REC', 'BK', 'PG', 'INST', 'DOC', 'APN', 'PIN', 'TMK', 'ORB', 'CRFN', 'CTF', 'OPR',
  'CFN', 'CPAP', 'DTD', 'RCVD', 'MTG', 'ASGN', 'REL', 'SAT', 'PRE', 'TBD', 'N/A',
]);
const LOWER_COMMENTS = new Set([...LOWER_PARTY,
  'into', 'onto', 'per', 'under', 'over', 'thru', 'through', 'than', 'then', 'is', 'are', 'was', 'were',
  'be', 'been', 'being', 'has', 'have', 'had', 'not', 'no', 'that', 'this', 'these', 'those', 'which',
  'it', 'its', 'if', 'via', 'nor', 'but', 'so', 'yet', 'up', 'out', 'off', 'about', 'after', 'before',
  'between', 'against', 'during', 'without', 'within', 'upon', 'also', 'all', 'any', 'each', 'both',
  'only', 'same', 'such', 'still', 'shall', 'will', 'may', 'can', 'should', 'would', 'does', 'do', 'did',
]);
// A short ALL-CAPS token right before one of these reads as a company acronym (ABC LENDING → ABC Lending)
const CORP_KEYWORDS = new Set(['LENDING', 'BANK', 'BANCORP', 'MORTGAGE', 'CORP', 'CORPORATION', 'INC', 'LLC', 'TRUST',
  'FINANCIAL', 'CAPITAL', 'HOLDINGS', 'GROUP', 'SERVICES', 'TITLE', 'FUNDING', 'HOME', 'LOANS', 'LOAN', 'CREDIT',
  'PARTNERS', 'COMPANY', 'CO', 'INVESTMENTS', 'PROPERTIES', 'REALTY', 'FEDERAL', 'SAVINGS', 'NATIONAL',
  'ASSOCIATES', 'ASSOCIATION', 'ENTERPRISES', 'VENTURES', 'MANAGEMENT', 'SOLUTIONS', 'SYSTEMS', 'INDUSTRIES',
  'DEVELOPMENT', 'CONSTRUCTION', 'BUILDERS', 'HOMES', 'EQUITY', 'ACCEPTANCE', 'FINANCE', 'FUND', 'LP', 'LLP',
  'PROPERTY', 'INVESTMENT', 'ESCROW', 'SETTLEMENT', 'UNION']);
const INITIALISM_RE = /^(?:[A-Za-z]\.)+[A-Za-z]?$/;
const SENTENCE_END_RE = /[.;:!?]$/;

const capSegment = (seg) => {
  if (!seg) return seg;
  if (seg.toUpperCase().startsWith('MC') && seg.length > 3) {
    return 'Mc' + seg[2].toUpperCase() + seg.slice(3).toLowerCase();
  }
  return seg[0].toUpperCase() + seg.slice(1).toLowerCase();
};

const capPart = (part) => {
  const segs = part.split("'");
  const out = [capSegment(segs[0])];
  for (const s of segs.slice(1)) out.push(s.length > 1 ? capSegment(s) : s.toLowerCase());
  return out.join("'");
};

// Short common words that must never be read as company acronyms (OLD National Bank → Old National Bank)
const NOT_ACRONYM = new Set(['OLD', 'KEY', 'ONE', 'NEW', 'BIG', 'SUN', 'RED', 'BAY', 'MID', 'TOP', 'ALL', 'OUR', 'WAY',
  'MAX', 'SEA', 'SKY', 'OAK', 'ELM', 'ASH', 'FOX', 'HUB', 'ART', 'AGE', 'ERA', 'ACE', 'GEM', 'ION', 'MY', 'GO', 'UP']);
const alphaOf = (core) => core.replace(/[^A-Za-z]/g, '');
// Acronym heuristics (only for ALL-CAPS tokens): 3–4 letters without a vowel (PNC, JPM, PHH),
// or a 2–3 letter token right before a corporate keyword (ABC Lending, TD Bank).
const isAcronymLike = (alpha, nextAlpha) => {
  if (alpha !== alpha.toUpperCase() || NOT_ACRONYM.has(alpha)) return false;
  if (alpha.length >= 3 && alpha.length <= 4 && !/[AEIOUY]/.test(alpha)) return true;
  return alpha.length >= 2 && alpha.length <= 3 && CORP_KEYWORDS.has((nextAlpha || '').toUpperCase());
};

const transformWord = (word, isStart, nextAlpha, upper, lower) => {
  const m = word.match(/^([^A-Za-z0-9]*)([\s\S]*?)([^A-Za-z0-9]*)$/);
  const [, prefix, core, suffix] = m;
  if (!core) return word;
  if (/\d/.test(core)) return word; // identifiers / dates / amounts untouched
  if (INITIALISM_RE.test(core)) return prefix + core.toUpperCase() + suffix; // N.A., U.S.
  const alpha = alphaOf(core);
  if (upper.has(alpha.toUpperCase()) || upper.has(core.toUpperCase())) return prefix + core.toUpperCase() + suffix;
  if (alpha.length === 1) return prefix + core.toUpperCase() + suffix; // initials (Fred A. Wilson)
  if (lower.has(alpha.toLowerCase())) return prefix + (isStart ? capPart(core) : core.toLowerCase()) + suffix;
  if (isAcronymLike(alpha, nextAlpha)) return prefix + core.toUpperCase() + suffix; // ABC Lending, PNC
  const parts = core.split(/([-/])/);
  return prefix + parts.map((p) => (p === '-' || p === '/' ? p : capPart(p))).join('') + suffix;
};

const runEngine = (text, upper, lower, sentenceMode) => {
  if (!text || typeof text !== 'string') return text;
  const chunks = text.split(/(\s+)/).filter(Boolean);
  const words = chunks.map((c, i) => (/^\s+$/.test(c) ? null : i)).filter((i) => i !== null);
  const out = [];
  let start = true;
  let prevWord = '';
  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i];
    if (/^\s+$/.test(chunk)) {
      out.push(chunk);
      if (chunk.includes('\n')) start = true;
      continue;
    }
    if (sentenceMode && SENTENCE_END_RE.test(prevWord)) start = true;
    const nextIdx = words[words.indexOf(i) + 1];
    const nextAlpha = nextIdx !== undefined ? alphaOf(chunks[nextIdx]) : '';
    out.push(transformWord(chunk, start, nextAlpha, upper, lower));
    start = false;
    prevWord = chunk;
  }
  return out.join('');
};

/** Rule P — party / name / short descriptive fields. Idempotent. */
export const smartProperCase = (text) => runEngine(text, UPPER_PARTY, LOWER_PARTY, false);

/** Rule C — free-form comments / notes: connectors lowercase except at sentence start. Idempotent. */
export const readableCaseComments = (text) => runEngine(text, UPPER_COMMENTS, LOWER_COMMENTS, true);

/** Predominantly ALL CAPS: ≥ 4 letters and ≥ 90 % of them uppercase. */
export const looksAllCaps = (v) => {
  const letters = String(v || '').replace(/[^A-Za-z]/g, '');
  if (letters.length < 4) return false;
  return letters.replace(/[^A-Z]/g, '').length / letters.length >= 0.9;
};

/** Apply a rule only to predominantly-ALL-CAPS input; mixed case is returned exactly as entered. */
export const autoCase = (value, rule) => (looksAllCaps(value) ? rule(value) : value);
export const autoPartyCase = (value) => autoCase(value, smartProperCase);
export const autoCommentsCase = (value) => autoCase(value, readableCaseComments);
